import { createSelector } from 'reselect'
import { isEmpty as isEmptyValidator } from 'lodash'
import moment from 'moment'
import { getWaiter } from 'redux-waiter'

import BENEFIT_TYPES from '@hixme/benefit-types'
import { ROLE_PLATFORM_HIXME_ADMIN, ROLE_BROKER } from '@hixme/role-policy'
import { selectors as authSelectors } from '@hixme/auth-ui'
import cart from 'store/modules/cart'
import guestUserIds from 'data/guestUserIds'

import {
  ADD_TO_MY_PROVIDERS,
  GET_USER_BENEFITS,
  NAME,
  REMOVE_ALL_FROM_PROVIDER,
  REMOVE_FROM_MY_PROVIDERS,
} from './constants'
import { getCartSummary } from '../cart/selectors'

const {
  BENEFIT_TYPE_ACCIDENT,
  BENEFIT_TYPE_BASIC_LIFE,
  BENEFIT_TYPE_CHIROPRACTIC,
  BENEFIT_TYPE_CRITICAL_ILLNESS,
  BENEFIT_TYPE_DCFSA,
  BENEFIT_TYPE_DENTAL,
  BENEFIT_TYPE_EMPLOYEE_ASSISTANCE,
  BENEFIT_TYPE_FSA,
  BENEFIT_TYPE_FTA,
  BENEFIT_TYPE_GROUP_HEALTH,
  BENEFIT_TYPE_HEALTH,
  BENEFIT_TYPE_HOSPITAL_INDEMNITY,
  BENEFIT_TYPE_INTERESTED,
  BENEFIT_TYPE_LEGAL_PLAN,
  BENEFIT_TYPE_LEGAL_SHIELD,
  BENEFIT_TYPE_LONG_TERM_DISABILITY,
  BENEFIT_TYPE_LPFSA,
  BENEFIT_TYPE_SHORT_TERM_DISABILITY,
  BENEFIT_TYPE_VISION,
  BENEFIT_TYPE_VOLUNTARY_AD_D,
  BENEFIT_TYPE_VOLUNTARY_LIFE,
  BENEFIT_TYPE_VOLUNTARY_LIFE_SIMPLE,
  BENEFIT_TYPE_VOLUNTARY_LONG_TERM_DISABILITY,
  BENEFIT_TYPE_VOLUNTARY_SHORT_TERM_DISABILITY,
} = BENEFIT_TYPES

/* USER SESSION */
export const getSessionSelector = createSelector(
  (state) => state[NAME],
  (session) => session
)

export const sessionErrorsSelector = createSelector(getSessionSelector, ({ errors }) => errors)

export const sessionIsUpdating = createSelector(
  (state) => state[NAME],
  (session) => session.updatingUser
)

export const sessionStarted = createSelector(
  (state) => state[NAME],
  (session) => session.userInitialized
)

export const getMemberships = createSelector(
  (state) => state[NAME],
  (session) => (Array.isArray(session.memberships) ? session.memberships : [])
)

/**
 * Raw enrollment paused flag (true even for exempt actors). Drives the PAUSED banner.
 */
export const getIsPaused = createSelector(
  (state) => state[NAME],
  (session) => !!session.isPaused
)

/**
 * Per-actor paused signal (exemption-baked-in server-side; false for test-users /
 * impersonated admins). Feeds getEnrollmentIsActiveSelector so a paused enrollment reads
 * as "no open enrollment" (which drives EnrollmentGate, the Home layout, and isEnrolling).
 * Also gates directly: the shop-health-plans bridge (useShopForHealthBridge), the
 * postBenefitToCart guard, and the QLE landing.
 */
export const getIsPausedForActor = createSelector(
  (state) => state[NAME],
  (session) => !!session.isPausedForActor
)

/* USER DATA */
export const getUser = createSelector(
  (state) => state[NAME],
  (session) => session.user
)

export const getClientPublicKey = createSelector(
  (state) => state[NAME],
  (session) => session.user.ClientPublicKey
)

export const getUserPayroll = createSelector(
  (state) => state[NAME],
  (state) => state.payroll
)

export const getUserName = createSelector(
  (state) => state[NAME],
  (session) => {
    const { FirstName = '', LastName = '' } = session.user || {}
    return `${FirstName} ${LastName}`
  }
)

export const getUserId = createSelector(
  (state) => state[NAME],
  (session) => (session ? session.userId : undefined)
)

export const getQleGroupNo = createSelector(
  (state) => state[NAME],
  (session) => session.qleGroupNo
)

export const getQleGroup = createSelector(
  (state) => state[NAME],
  (session) => session.qleGroup
)

export const isGuestUser = createSelector(
  (state) => getUserId(state),
  (userId) => guestUserIds.includes(userId)
)

export const isImpersonating = createSelector(
  (state) => {
    const userId = getUserId(state)
    const loggedInUser = authSelectors.getCredentials(state)
    // Admin/broker Login-As only. Employee multi-employer switches exchange
    // Cognito credentials, so userId === personPublicKey and this stays false.
    const isImpersonating =
      userId !== loggedInUser.personPublicKey &&
      [ROLE_PLATFORM_HIXME_ADMIN, ROLE_BROKER].indexOf(loggedInUser.userRole) > -1
    return isImpersonating
  },
  (isImpersonating) => isImpersonating
)

/**
 * True when the active Cognito identity is the employee's primary/home
 * membership (i.e. no person-exchange has swapped in a secondary employer's
 * tokens). Used to gate account actions that mutate the Cognito user itself
 * (e.g. change password) so they can't accidentally apply to a secondary
 * membership's credentials.
 *
 * Empty memberships → true: EPS returns [] when cross-employer is off / there
 * is nothing to switch, so the user cannot be on a secondary identity.
 * Fail-closed when memberships exist but none is primary (synthetic bootstrap
 * rows, or EPS with no primary flag).
 */
export const isHomeCognitoIdentity = createSelector(
  (state) => authSelectors.getCredentials(state),
  (state) => getMemberships(state),
  (credentials, memberships) => {
    if (isEmptyValidator(memberships)) return true
    const primary = memberships.find((membership) => membership.isPrimary)
    if (!primary) return false
    return primary.personId === credentials?.username
  }
)

export const isHixmeAdmin = createSelector(
  (state) => {
    const loggedInUser = authSelectors.getCredentials(state)
    const isHixmeAdmin = loggedInUser.userRole === ROLE_PLATFORM_HIXME_ADMIN
    return isHixmeAdmin
  },
  (isHixmeAdmin) => isHixmeAdmin
)

/* ENROLLMENT RESPONSES */
export const getEnrollmentResponses = createSelector(
  (state) => state[NAME],
  (userSession) => {
    const sorted = userSession.enrollmentResponses.sort(
      (a, b) => parseFloat(a.QuestionNumber) - parseFloat(b.QuestionNumber)
    )
    return sorted
  }
)

/* PEVIOUS ENROLLMENT */
export const getPreviousEnrollment = createSelector(
  (state) => state[NAME],
  (userSession) => userSession.previousEnrollment
)

/* ENROLLMENT SESSION */
export const getEnrollmentSession = createSelector(getSessionSelector, (userSession) => ({
  ...userSession.enrollmentSession,
  EnrollmentCutoffTime: userSession.enrollmentCutoffTime,
}))

export const getEnrollmentPublicKey = createSelector(getEnrollmentSession, (session) => session.Id)

export const getTermsAndConditionsAgreed = createSelector(
  getEnrollmentSession,
  (session) => session.TermsAndConditionsAgreed
)

export const isCartCommitted = createSelector(
  getEnrollmentSession,
  (session) => session.CartCommitted || false
)

export const getBundleGroups = createSelector(
  getEnrollmentSession,
  (session) => session.BundleGroups
)

export const getFamilyGroups = createSelector(
  getEnrollmentSession,
  (session) => session.FamilyGroups || {}
)

export const hasGroupToShop = createSelector(
  getEnrollmentSession,
  (session) =>
    session.FamilyGroups &&
    session.FamilyGroups.FamilyGroups &&
    session.FamilyGroups.FamilyGroups.filter((group) => group.GroupType !== 'MEDICARE').some(
      (group) => !group.SelectedPlanName
    )
)

export const getGroups = createSelector(
  getEnrollmentSession,
  (session) => session.FamilyGroups && session.FamilyGroups.FamilyGroups
)

export const getHealthGroups = createSelector(
  getEnrollmentSession,
  (session) =>
    session.FamilyGroups &&
    session.FamilyGroups.FamilyGroups.filter(
      (group) =>
        group.GroupType === 'HEALTH_INSURANCE' || group.GroupType === 'HEALTH_INSURANCE_SINGLE'
    )
)

// Normalized per-member health roster for the cart "My Health Insurance" card.
// Derives each family member's status from FamilyGroups (the same source GroupsView
// consumes) so the card can list everyone even when no plan is selected yet. Every
// member is emitted — including covered ones — so nobody is dropped; the cart card
// dedupes covered members already shown in a Benefits block against
// Benefits[].Persons[].Id. Aged-out / ineligible NOT_INCLUDED members are omitted.
// Rows: { Id, FirstName, LastName, status } where status is
// 'covered' | 'medicare' | 'no-coverage-selected' | 'declined'. No-coverage rows also
// carry `groupId` so their Shop button can bridge straight to that group. Health-group
// members who are Medicare-eligible (i.e. have declined Medicare) also carry
// `medicareDeclined: true` so the card can show a "Will not enroll in Medicare" note.
// 'medicare' is reserved for members of an actual MEDICARE group: a Medicare-eligible
// member sitting in a HEALTH_INSURANCE group has *declined* Medicare (that's why they're
// in a health group at all), so they're treated as a normal shoppable health-group member
// here — matching GroupsView and the /continue resolver, which both treat them the same.
export const getHealthRosterMembers = createSelector(getFamilyGroups, (familyGroups) => {
  const groups = (familyGroups && familyGroups.FamilyGroups) || []
  const declined = (familyGroups && familyGroups.DeclinedFamilyMembers) || []

  const toRow = (member, status) => ({
    Id: member.Id,
    FirstName: member.FirstName,
    LastName: member.LastName,
    status,
  })

  const rows = []
  groups.forEach((group) => {
    const members = group.FamilyMembers || []
    if (group.GroupType === 'MEDICARE') {
      members.forEach((member) => rows.push(toRow(member, 'medicare')))
    } else if (
      group.GroupType === 'HEALTH_INSURANCE' ||
      group.GroupType === 'HEALTH_INSURANCE_SINGLE'
    ) {
      members.forEach((member) => {
        // Classify every health-group member by the group's plan selection — including a
        // Medicare-eligible member, who lands in a health group precisely because they
        // declined Medicare and so still needs a health decision (do NOT mislabel them
        // 'medicare'/auto-selected). 'medicare' is reserved for the MEDICARE branch above.
        const status = group.SelectedPlanName ? 'covered' : 'no-coverage-selected'
        const row = toRow(member, status)
        // No-coverage members have a shoppable group, so the cart Shop button bridges
        // straight to that group (or the legacy shop page); carry the group id for it.
        if (status === 'no-coverage-selected') row.groupId = group.Id
        // A Medicare-eligible member in a health group has declined Medicare; flag it so the
        // cart card can surface "Will not enroll in Medicare" (matching GroupsView), whether
        // they've since selected a plan (covered) or not (no-coverage-selected).
        if (member.MedicareEligible) row.medicareDeclined = true
        rows.push(row)
      })
    }
    // NOT_INCLUDED (aged-out / ineligible) → omitted
  })
  declined.forEach((member) => rows.push(toRow(member, 'declined')))

  return rows
})

export const getRecommendedBundleGroups = createSelector(
  getEnrollmentSession,
  (session) => session.HixmeRecommendedBundleGroups
)

export const getIsCustomGroups = createSelector(
  getEnrollmentSession,
  (session) => session.IsCustomGroups
)

export const getEnrollmentIsActiveSelector = createSelector(
  getEnrollmentSession,
  getIsPausedForActor,
  (session, isPausedForActor) => {
    // A paused-for-actor enrollment behaves as "no open enrollment": treat the
    // window as closed so the whole not-in-OE path applies (Home post-enrollment layout,
    // EnrollmentGate, isEnrolling) — the same path a genuinely-closed window already uses.
    if (isPausedForActor) return true
    const now = moment()
    const startDate = moment(session.EnrollmentStartDate)
    const endDate = moment(session.EnrollmentEndDate).hour(
      session.EnrollmentCutoffTime.split(':')[0]
    )
    return now.isAfter(endDate) || now.isBefore(startDate, 'day')
  }
)

export const isBenefitActive = createSelector(
  getEnrollmentSession,
  ({ BenefitEndDate, BenefitEffectiveDate }) => {
    const now = moment().format('YYYY-MM-DD')
    if (!BenefitEndDate || !BenefitEffectiveDate) {
      return false
    }
    const benefitEndDate = moment(BenefitEndDate, 'YYYY-MM-DD')
    const benefitStartDate = moment(BenefitEffectiveDate, 'YYYY-MM-DD')
    return moment(now).isBetween(benefitStartDate, benefitEndDate, null, '[]')
  }
)

export const getIntentToElectHsa = createSelector(
  getEnrollmentSession,
  (session) => session.IntentToElectHSA
)

export const isQleEnrollment = createSelector(
  getEnrollmentSession,
  (session) => !isEmptyValidator(session.QualifyingLifeEvent)
)

export const isEnrolling = createSelector(
  getEnrollmentIsActiveSelector,
  (state) => isCartCommitted(state),
  (EnrollmentIsActive, CartCommitted) => !EnrollmentIsActive && !CartCommitted
)

export const getMedicareEnrollment = createSelector(getEnrollmentSession, (enrollmentSession) => {
  return {
    EnrollmentPublicKey: enrollmentSession.Id,
    MedicareEnrollment:
      enrollmentSession.MedicareEnrollment || enrollmentSession.MedicareEnrollments,
    MedicareCoverage: enrollmentSession.Medicare,
  }
})

export const allowMedicareEnrollmentExperience = createSelector(
  getMedicareEnrollment,
  (medicareEnrollment) => {
    const enrollments = medicareEnrollment?.MedicareEnrollment || []
    return enrollments
      .map((item) => item?.Status || item?.status)
      .some((status) => typeof status === 'string' && status.toLowerCase() === 'open')
  }
)

/**
 * Same as isEnrolling but excludes QLE enrolling. Also false when paused-for-actor,
 * because getEnrollmentIsActiveSelector treats a paused enrollment as a closed window.
 */
export const isEnrollingExcludeQLE = createSelector(
  getEnrollmentIsActiveSelector,
  (state) => isCartCommitted(state),
  (state) => isQleEnrollment(state),
  (EnrollmentIsActive, CartCommitted, isQleEnrollment) =>
    !EnrollmentIsActive && !CartCommitted && !isQleEnrollment
)

export const isPreEnrollment = createSelector(getEnrollmentSession, (session) => {
  const enrollmentDate = moment(session.EnrollmentStartDate)
  const diff = moment().diff(enrollmentDate, 'seconds')
  return diff < 0
})

export const getEnrollmentStartDateSelector = createSelector(getEnrollmentSession, (session) =>
  moment(session.EnrollmentStartDate).format('MM/DD/YYYY')
)

export const getBenefitEffectiveDateSelector = createSelector(getEnrollmentSession, (session) =>
  moment(session.BenefitEffectiveDate).format('MM/DD/YYYY')
)

export const getBenefitEffectiveYearSelector = createSelector(getEnrollmentSession, (session) =>
  moment(session.BenefitEffectiveDate, 'YYYY/MM/DD').year()
)

export const getBenefitEndDateSelector = createSelector(getEnrollmentSession, (session) =>
  moment(session.BenefitEndDate).format('MM/DD/YYYY')
)

export const getEnrollmentEndDateSelector = createSelector(getEnrollmentSession, (session) => {
  if (!session.EnrollmentEndDate || !session.EnrollmentCutoffTime) return ''
  return moment(session.EnrollmentEndDate)
    .hour(session.EnrollmentCutoffTime.split(':')[0])
    .format('MM/DD/YYYY')
})

// Check if enrollment is not closed (same-day: before the cutoff time in the worker's local
// timezone; earlier days: always open; later days: always closed).
// Uses the same local-time comparison as getEnrollmentIsActiveSelector so the card and the
// rest of enrollme agree about when the window ends.
export const isEnrollmentNotClosedSelector = createSelector(getEnrollmentSession, (session) => {
  if (!session.EnrollmentEndDate || !session.EnrollmentCutoffTime) return false

  const cutoffHour = parseInt(session.EnrollmentCutoffTime.split(':')[0], 10)

  // Validate cutoff hour
  if (isNaN(cutoffHour) || cutoffHour < 0 || cutoffHour > 23) return false

  // Build the deadline in browser-local time: end date at the cutoff hour (e.g. 17:00 local).
  // Date-only strings like "2026-10-15" parse as local midnight; .hour() then sets the hour.
  // This matches getEnrollmentIsActiveSelector and is DST-correct: 5pm PDT in October,
  // 5pm PST in November, with no hardcoded UTC offset.
  const endDate = moment(session.EnrollmentEndDate).hour(cutoffHour)

  return !moment().isAfter(endDate)
})

// Format enrollment end date and time (e.g., "5pm on November 24th")
export const getEnrollmentEndDateTimeFormattedSelector = createSelector(
  getEnrollmentSession,
  (session) => {
    if (!session.EnrollmentEndDate || !session.EnrollmentCutoffTime) return ''

    const cutoffTimeParts = session.EnrollmentCutoffTime.split(':')
    const cutoffHour = parseInt(cutoffTimeParts[0], 10)

    // Validate cutoff hour
    if (isNaN(cutoffHour) || cutoffHour < 0 || cutoffHour > 23) return ''

    let timeStr
    if (cutoffHour === 0) {
      timeStr = '12am'
    } else if (cutoffHour === 12) {
      timeStr = '12pm'
    } else if (cutoffHour > 12) {
      timeStr = `${cutoffHour - 12}pm`
    } else {
      timeStr = `${cutoffHour}am`
    }

    const dateStr = moment(session.EnrollmentEndDate).format('MMMM Do')
    return `${timeStr} on ${dateStr}`
  }
)

export const getDaysUntilEnrollmentStartsSelector = createSelector(
  getEnrollmentSession,
  (session) => {
    const todaysDate = moment()
    const enrollmentStartDate = moment(session.EnrollmentStartDate)
    const diff = enrollmentStartDate.diff(todaysDate, 'days')
    return diff
  }
)

export const getTimeRemainingMessageSelector = createSelector(getEnrollmentSession, (session) => {
  const endDate = moment(session.EnrollmentEndDate).hour(session.EnrollmentCutoffTime.split(':')[0])
  const momentStr = moment(endDate).fromNow(true)
  const retMessage = `You have ${momentStr} to enroll or decline this insurance`
  return retMessage
})

export const getTimeRemainingFlexibleSpendingSelector = createSelector(
  getEnrollmentSession,
  (session) => {
    const benefitsEffectiveDate = moment(session.BenefitsEffectiveDate)
    const endOfYear = moment(benefitsEffectiveDate).endOf('year')

    const diff = endOfYear.to(benefitsEffectiveDate, true)
    const monthDiff = endOfYear.diff(benefitsEffectiveDate, 'months')
    if (monthDiff === 11) {
      return null
    }
    return `You have ${diff} remaining to fund this account via paycheck deductions.`
  }
)

export const getEnrollmentDurationMessageSelector = createSelector(
  getEnrollmentSession,
  (session) => {
    const info = {}
    const now = moment().format('YYYY-MM-DD HH:mm:ss')
    const startDate = moment(session.EnrollmentStartDate)
    const endDate = moment(session.EnrollmentEndDate).hour(
      session.EnrollmentCutoffTime.split(':')[0]
    )
    const enrollmentActive = moment(now).isBetween(startDate, endDate, null, '[]')
    // Removed `info.isEnrolling = enrollmentActive`
    // use `isEnrollingExcludeQLE` or `isEnrolling` selector for consistency
    // and select it separately for when needed

    if (enrollmentActive) {
      info.message = `Enrollment ends in ${endDate.toNow(true)}`
      info.shortMessage = endDate.toNow(true)
    } else {
      info.message = `Enrollment starts ${startDate.fromNow()}`
    }
    return info
  }
)

/* CLIENT SETTINGS */
export const getClientSettings = createSelector(
  getSessionSelector,
  (userSession) => userSession.clientSettings
)

export const getClientId = createSelector(getClientSettings, (settings) => settings.Id)

export const getCompanyName = createSelector(
  getClientSettings,
  (clientSettings) => clientSettings.CompanyName
)

export const getShowNewExperience = createSelector(
  getClientSettings,
  (clientSettings) => clientSettings.ShowNewExperience || false
)

export const getIsClientProspect = createSelector(
  getClientSettings,
  (settings) => settings.IsProspect
)

export const hasSeenProviderLookupQuestion = createSelector(
  getEnrollmentSession,
  (session) => session.ProviderSearchCheck
)

export const hasNeverAnsweredProviderLookupQuestion = createSelector(
  hasSeenProviderLookupQuestion,
  (providerSearchCheck) => providerSearchCheck === undefined
)

export const hasFamilyGroups = createSelector(
  getEnrollmentSession,
  (session) => !isEmptyValidator(session.FamilyGroups)
)

export const isCreatingOrUpdating = createSelector(
  (state) => state.waiter,
  (waiterStore = {}) => {
    const waitersKeys = Object.keys(waiterStore).filter(
      (waiterName) =>
        waiterName.includes(ADD_TO_MY_PROVIDERS) ||
        waiterName.includes(REMOVE_FROM_MY_PROVIDERS) ||
        waiterName.includes(REMOVE_ALL_FROM_PROVIDER)
    )
    const isEmpty = waitersKeys.length === 0
    if (isEmpty) return false

    return waitersKeys.some((key) => (waiterStore[key] || {}).isPending)
  }
)

export const getClientBenefitsSummary = createSelector(getClientSettings, (settings) => {
  // Mid-bootstrap loadClientSettings({}) leaves BenefitCounts undefined while
  // Home stays mounted — coerce so .reduce is always safe.
  const benefitCounts = Array.isArray(settings.BenefitCounts) ? settings.BenefitCounts : []

  const additionalBenefits = []
  const hasBenefits = benefitCounts.reduce((prev, benefit) => {
    if (benefit.BenefitType === BENEFIT_TYPE_DENTAL) {
      return { ...prev, hasDental: true }
    }
    if (benefit.BenefitType === BENEFIT_TYPE_VISION) {
      return { ...prev, hasVision: true }
    }
    if (benefit.BenefitType === BENEFIT_TYPE_GROUP_HEALTH) {
      return { ...prev, hasGroupHealth: true }
    }
    if (benefit.BenefitType === BENEFIT_TYPE_HEALTH) {
      return { ...prev, hasHealthBundle: true }
    }
    additionalBenefits.push(benefit)
    return prev
  }, {})

  return {
    ...hasBenefits,
    hasAdditionalBenefits: additionalBenefits.length > 0,
  }
})

export const getCompanyLogoUrlSelector = createSelector(
  getClientSettings,
  (settings) => settings.CompanyLogoUrl
)

export const getHelpPhoneNumber = createSelector(
  getClientSettings,
  (settings) => settings.CustomerServicePhoneNumber
)

/* CLIENT BENEFITS ENROLLMENT */
export const getClientBenefitsEnrollment = createSelector(
  getSessionSelector,
  (userSession) => userSession.clientBenefitsEnrollment
)

export const isHSASwap = createSelector(
  getClientBenefitsEnrollment,
  (clientBenefitsEnrollment) => clientBenefitsEnrollment.HSASwap
)

// Client benefit configurations
export const benefitIsAvailable = createSelector(
  getClientSettings,
  (clientSettings) => (benefitType) => {
    const BenefitCounts = clientSettings.BenefitCounts
    if (!BenefitCounts) {
      return false
    }

    return !!BenefitCounts.find((item) => item.BenefitType === benefitType) // return bool
  }
)

/* ICHRA */
export const getICHRAAvailablity = createSelector(
  (state) => state[NAME],
  (userSession) => userSession.ichra.available
)

/* Providers */
export const getUserLocation = createSelector(
  (state) => state[NAME],
  (userSession) => userSession.location
)

export const getUserProviders = createSelector(
  (state) => state[NAME],
  (userSession) => userSession.providers
)

export const getSelectedPersonsByProviderId = (providerId, state) =>
  createSelector(
    () => state[NAME],
    (userSession) => {
      const provider = userSession.providers.find((p) => p.id === providerId)
      if (!provider) return []
      return provider.selected
    }
  )(state)

export const isPersonSelectedByProviderId = (providerId, personPublicKey, state) =>
  createSelector(
    () => state[NAME],
    (userSession) => {
      const provider = userSession.providers.find((p) => p.id === providerId)
      if (!provider) return false
      return provider.selected.some((s) => s.id === personPublicKey)
    }
  )(state)

/* BENEFITS */
export const interestedBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_INTERESTED)

export const visionBenefitIsAvailable = (state) => benefitIsAvailable(state)(BENEFIT_TYPE_VISION)

export const dentalBenefitIsAvailable = (state) => benefitIsAvailable(state)(BENEFIT_TYPE_DENTAL)

export const employeeAssistanceBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_EMPLOYEE_ASSISTANCE)

export const healthBundleBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_HEALTH)

export const groupHealthBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_GROUP_HEALTH)

export const FSABenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_FSA) &&
  !cart.selectors.getHSABenefitProduct(state) &&
  !getEnrollmentSession(state).IntentToElectHSA

export const LPFSABenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_LPFSA) &&
  (cart.selectors.getHSABenefitProduct(state) || getEnrollmentSession(state).IntentToElectHSA)

export const DCFSABenefitIsAvailable = (state) => benefitIsAvailable(state)(BENEFIT_TYPE_DCFSA)

export const FTABenefitIsAvailable = (state) => benefitIsAvailable(state)(BENEFIT_TYPE_FTA)

export const basicLifeBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_BASIC_LIFE)

export const voluntaryLifeBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_VOLUNTARY_LIFE)

export const voluntaryLifeSimpleBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_VOLUNTARY_LIFE_SIMPLE)

export const shortTermDisabilityBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_SHORT_TERM_DISABILITY)

export const longTermDisabilityBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_LONG_TERM_DISABILITY)

export const voluntaryLongTermDisabilityBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_VOLUNTARY_LONG_TERM_DISABILITY)

export const voluntaryShortTermDisabilityBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_VOLUNTARY_SHORT_TERM_DISABILITY)

export const voluntaryADDBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_VOLUNTARY_AD_D)

export const legalPlanBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_LEGAL_PLAN)

export const legalShieldBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_LEGAL_SHIELD)

export const hospitalIndemnityBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_HOSPITAL_INDEMNITY)

export const accidentBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_ACCIDENT)

export const chiropracticBenefitIsAvailable = (state) => {
  const employeeHasMedical = cart.selectors.employeeHasMedicalProduct(state)
  return benefitIsAvailable(state)(BENEFIT_TYPE_CHIROPRACTIC) && employeeHasMedical
}

export const criticalIllnessBenefitIsAvailable = (state) =>
  benefitIsAvailable(state)(BENEFIT_TYPE_CRITICAL_ILLNESS)

export const isQLEAvailable = (state) => {
  const { Benefits } = getWaiter(state, `${GET_USER_BENEFITS}_`).response || {
    Benefits: [],
  }
  const enrollmentSession = getEnrollmentSession(state)
  const now = moment()
  const enrollmentStartDate = moment(enrollmentSession.EnrollmentStartDate)
  const enrollmentEndDate = moment(enrollmentSession.EnrollmentEndDate) // disregard enrollmentSession.EnrollmentCutoffTime here, since QLE will fail due to overlapping enrollment
  const isWithinEnrollmentPeriod =
    now.isSameOrAfter(enrollmentStartDate, 'hour') && now.isSameOrBefore(enrollmentEndDate, 'hour')

  return (
    Boolean(enrollmentSession.QualifyingLifeEvent) ||
    (Benefits &&
      Benefits.find((benefit) => {
        return (
          [BENEFIT_TYPE_DENTAL, BENEFIT_TYPE_HEALTH, BENEFIT_TYPE_VISION].includes(
            benefit.BenefitType
          ) && moment().isSameOrBefore(benefit.BenefitEndDate, 'day')
        )
      }) &&
      !isWithinEnrollmentPeriod)
  )
}

// Uses current enrollment because previous one doesn't exists in QLE Removal flow once the new enrollment created is not active yet.
export const getPreviousPaycheck = createSelector(getEnrollmentSession, (enrollment) => {
  return getCartSummary({
    Cart: {
      cart: enrollment?.Cart ?? [],
    },
  })?.total
})

export const getPlanMap = (state) => {
  const { Benefits } = getWaiter(state, `${GET_USER_BENEFITS}_`).response || {
    Benefits: [],
  }
  const healthBenefits = Benefits.filter((benefit) => benefit.BenefitType === BENEFIT_TYPE_HEALTH)
  return healthBenefits.reduce((prev, benefit) => {
    const persons = benefit.Persons.reduce((acc, person) => {
      return {
        ...acc,
        [person.Id]: {
          Carrier: benefit.CarrierName,
          ...person,
          FullName: `${person.FirstName} ${person.LastName}`,
        },
      }
    }, [])
    return { ...prev, ...persons }
  }, {})
}

export const getBenefitById = (id, state) =>
  createSelector(getPlanMap, (planMap) => planMap[id])(state)
